import React, { useCallback, useRef } from 'react';

import { QStack, QText } from '@captivateiq/quotient';

import { useAIShellContext } from '@captivateiq/assistant/AIShellContext';
import type { AgentChoiceOption } from '@captivateiq/assistant/types';

type MultipleChoiceOptionsProps = {
  choices: AgentChoiceOption[];
};

export const MultipleChoiceOptions = ({ choices }: MultipleChoiceOptionsProps) => {
  const { send, agentThinking } = useAIShellContext();
  const sendingRef = useRef(false);

  const handleClick = useCallback(
    async (choice: AgentChoiceOption) => {
      if (agentThinking || sendingRef.current) return;
      sendingRef.current = true;
      try {
        await send(choice.label);
      } finally {
        sendingRef.current = false;
      }
    },
    [send, agentThinking],
  );

  if (choices.length === 0) return null;

  return (
    <QStack direction="column" spacing="4px" sx={{ pt: 0.5 }}>
      {choices.map((choice, index) => (
        <QStack
          aria-disabled={agentThinking}
          direction="row"
          key={choice.label}
          role="button"
          spacing="8px"
          sx={{
            alignItems: 'flex-start',
            backgroundColor: 'white',
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: '6px',
            cursor: agentThinking ? 'default' : 'pointer',
            opacity: agentThinking ? 0.5 : 1,
            padding: '6px 10px',
            '&:hover': agentThinking
              ? {}
              : {
                  backgroundColor: 'grey.100',
                  borderColor: 'primary.main',
                },
          }}
          tabIndex={agentThinking ? -1 : 0}
          onClick={() => handleClick(choice)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleClick(choice);
            }
          }}
        >
          <QText sx={{ color: 'grey.500', fontSize: '11px', fontWeight: 600, lineHeight: 1.4, flexShrink: 0 }}>
            {index + 1}.
          </QText>
          <QStack direction="column" spacing="1px" sx={{ minWidth: 0 }}>
            <QText sx={{ color: 'grey.700', fontSize: '12px', fontWeight: 600, lineHeight: 1.4 }}>{choice.label}</QText>
            {choice.description && (
              <QText sx={{ color: 'grey.500', fontSize: '11px', lineHeight: 1.3 }}>{choice.description}</QText>
            )}
          </QStack>
        </QStack>
      ))}
    </QStack>
  );
};
